import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Box, Grid, Tabs, Tab, Button, TextField, InputAdornment } from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material_modern'
import { useTheme } from '@mui/styles'
import { useCreatePriceSheetMutation } from 'services/priceSheets'
import CreatePriceListModal from 'components/generic/modals/dialog-modal/CreatePriceListModal'
import Loading from 'components/generic/Loading'
import { dispatchSnackbar } from 'utils/helpers'
import { PRICE_SHEET_TYPES } from 'utils/constants'
import { useContentHeight } from 'utils/custom-hooks/useContentHeight'

const PriceListsSideNav = ({ selectedPriceListId, setSelectedPriceListId, priceSheetList }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [openModal, setOpenModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { contentHeight } = useContentHeight()
  const [createPriceSheet, { isFetching: isFetchingCreatePriceSheet }] = useCreatePriceSheetMutation()

  const onSave = useCallback(async priceListName => {
    setOpenModal(false)

    await createPriceSheet({
      priceSheet: {
        displayName: priceListName,
        entriesObj: [],
        type: PRICE_SHEET_TYPES.PRICE_LIST,
      },
    })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Price list created successfully.')
      })
      .catch(error => {
        const errorMessage = error?.data?.error || 'An error occurred while creating the price list.'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }, [])

  const filteredPriceLists = priceSheetList.filter(priceList =>
    priceList.displayName?.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <>
      {isFetchingCreatePriceSheet && <Loading />}
      <Grid item xs={3} pl={1} pr={0.5} sx={{ height: contentHeight }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            backgroundColor: 'background.default',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
          pt={2}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              pb: 2,
              rowGap: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => setOpenModal(true)}
              sx={{
                width: '100%',
              }}
            >
              Create Price List
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search Price Lists"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{
                width: '100%',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              overflowY: 'auto',
              flex: 1,
              pb: 2,
            }}
          >
            {filteredPriceLists.length > 0 && (
              <Tabs orientation="vertical" value={selectedPriceListId || false} TabIndicatorProps={{ hidden: false }}>
                {filteredPriceLists.map((priceList, index) => (
                  <Tab
                    key={priceList.id}
                    label={priceList.displayName}
                    value={priceList.id}
                    onClick={() => {
                      if (priceList.id === selectedPriceListId) {
                        setSelectedPriceListId(false)
                      } else {
                        setSelectedPriceListId(priceList.id)
                      }
                    }}
                    sx={{
                      maxWidth: '100%',
                      borderTop: index === 0 ? 1 : 'unset',
                      borderBottom: 1,
                      borderColor: 'grey.400',
                      textTransform: 'none',
                      alignItems: 'flex-start',
                      padding: '24px 16px 24px 16px',
                      transition: 'background 0.2s ease-in-out',
                      fontWeight: '400',
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.lightBg,
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.lightBg,
                        color: theme.palette.text.primary,
                        fontWeight: '600',
                      },
                    }}
                  />
                ))}
              </Tabs>
            )}
          </Box>
        </Box>
      </Grid>
      <CreatePriceListModal
        title="Create Price List"
        buttonTitle="Create"
        hideDividers
        open={openModal}
        onSave={onSave}
        onClose={() => setOpenModal(false)}
        priceLists={priceSheetList}
      />
    </>
  )
}

PriceListsSideNav.propTypes = {
  selectedPriceListId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setSelectedPriceListId: PropTypes.func.isRequired,
  priceSheetList: PropTypes.array,
}

PriceListsSideNav.defaultProps = {
  selectedPriceListId: '',
  priceSheetList: [],
}

export default PriceListsSideNav
